import { useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

// local imports - types
import warningImage from 'assets/images/warning-image.svg'
import calendarLoading from 'assets/icons/loading-calendar.png'
import DateUtil from 'utils/DateUtil'
import Loader from 'components/ui/Loader'
import { IFilteredLactationRoomList } from 'types/LactationType'
import WeekSlider from 'components/home/on-campus-services/health-service/appointment-scheduler/WeekSlider'
import ScheduleSidebar from 'components/home/on-campus-services/lactation-room/reservation-scheduler/ScheduleSidebar'
import Scheduler from 'components/home/on-campus-services/lactation-room/reservation-scheduler/Scheduler'
import ReservationModalHeader from 'components/home/on-campus-services/lactation-room/reservation-scheduler/ReservationModalHeader'
import useGetLactationRoomResTable from 'components/home/on-campus-services/lactation-room/hooks/use-get-lactation-room-res-table'
import {
  ActionArea,
  CancelButton,
  ConfirmButton,
} from 'components/home/on-campus-services/coiffeur/reservation-scheduler/styled'
import NotificationDialog from 'components/ui/NotificationDialog'

/* Types */
interface IModalFooterProps {
  disabled: boolean
  onCancel: () => void
  onSave: () => void
}

interface IAppointmentModalProps {
  cancelReservationModal: () => void
  filterDate: Date
  selectedRoom: IFilteredLactationRoomList
  open: boolean
  closeModal: () => void
  openModal: () => void
  showNotification: () => void
  selectedDateStr?: string
}

export default function LactationResModalWrapper({
  cancelReservationModal,
  filterDate: dateFromList,
  selectedRoom,
  open,
  closeModal,
  openModal,
  showNotification,
  selectedDateStr,
}: IAppointmentModalProps) {
  const { t } = useTranslation()

  /* Lactation Room - reservationDate filter value for appointment date filtering etc. */
  const [filterDate, setFilterDate] = useState<Date>(dateFromList)
  const lastFilteredDate = DateUtil.getWeekStartEndFromDate(dateFromList)
  const [diffDayNotification, setDiffDayNotification] = useState(false)
  const isSelectedDifferentDate = moment(filterDate).isSame(dateFromList, 'day')

  /* GET Selected Lactation Room's Reservation Table List | Custom Hook  */
  const salonLocationId = selectedRoom?.floor?.locationId
  const lactationRoomId = selectedRoom?.id
  const {
    availableDates,
    tableWeek,
    setTableWeek,
    selectedReservation,
    setSelectedReservation,
    sidebarVisible,
    setSidebarVisible,
    loading,
    dailyAppointments,
  } = useGetLactationRoomResTable(salonLocationId, lactationRoomId, dateFromList, true)

  /* Open Reservation Sidebar */
  const openReservationSidebar = () => {
    if (isSelectedDifferentDate) {
      setSidebarVisible(true)
    } else {
      setDiffDayNotification(true)
    }
    closeModal()
  }
  /* Close Sidebar and reset states */
  const closeSidebarAfterSave = () => {
    showNotification()
    setSidebarVisible(false)
    closeModal()
  }

  /* Close with cancel */
  const handleCloseOnCancel = () => {
    cancelReservationModal()
    setFilterDate(dateFromList)
    setSelectedReservation(undefined)
    setTableWeek(lastFilteredDate)
  }

  /* Different Day Reservation - Notification Dialog Methods */
  const handleAcceptDiffDayReservation = () => {
    setDiffDayNotification(false)
    setSidebarVisible(true)
    closeModal()
  }
  const handleRejectDiffDayReservation = () => {
    setDiffDayNotification(false)
    openModal()
  }

  return (
    <>
      <Dialog
        id="appointmentModal"
        blockScroll
        keepInViewport
        closeOnEscape={false}
        closable={true}
        focusOnShow={false}
        header={<span>{`${t('lactation-res-modal.lactation-reservation')}`} (2/2)</span>}
        draggable={false}
        resizable={false}
        visible={open}
        style={{ width: '95vw', height: '95vh' }}
        breakpoints={{ '1536px': '95vw', '1280': '95vw' }}
        onHide={handleCloseOnCancel}
        footer={
          <FooterComponent
            onSave={openReservationSidebar}
            onCancel={handleCloseOnCancel}
            disabled={!selectedReservation}
          />
        }>
        {/* Modal Header */}
        <ReservationModalHeader
          selectedDateStr={selectedDateStr}
          filterDate={filterDate}
          availableDateRange={availableDates}
          setFilterDate={setFilterDate}
          room={selectedRoom}
          setTableWeek={setTableWeek}
        />

        {/* Week Change Slider */}
        <WeekSlider availableDateRange={availableDates} tableWeek={tableWeek} setTableWeek={setTableWeek} />

        {/* Content - Calendar View */}
        {loading ? (
          <Loader src={calendarLoading} />
        ) : (
          <Scheduler
            filterDate={filterDate}
            setFilterDate={setFilterDate}
            tableWeek={tableWeek}
            dailyAppointments={dailyAppointments}
            selectedAppointment={selectedReservation}
            setSelectedAppointment={setSelectedReservation}
          />
        )}
      </Dialog>

      {/* Schedule - Save Appointment Sidebar */}
      {!!selectedReservation && (
        <ScheduleSidebar
          openModal={openModal}
          visible={sidebarVisible}
          setVisible={setSidebarVisible}
          closeAfterSave={closeSidebarAfterSave}
          roomInfo={selectedRoom}
          reservationInfo={selectedReservation}
        />
      )}

      <NotificationDialog
        display={diffDayNotification}
        imgSrc={warningImage}
        goHomeButton={true}
        extraButton={true}
        title={t('overlaypanel-texts.reservation-day-changed')}
        content={t('overlaypanel-texts.reservation-day-change-text')}
        firstButtonLabel={t('buttons.yes')}
        secondButtonLabel={t('buttons.no')}
        onHide={handleRejectDiffDayReservation}
        onSubmit={handleAcceptDiffDayReservation}
        selectedType={null}
        setSelectedType={undefined}
        types={[]}
      />
    </>
  )
}

function FooterComponent({ onSave, onCancel, disabled }: IModalFooterProps) {
  const { t } = useTranslation()
  return (
    <ActionArea>
      <CancelButton id="closeModal" className="p-button-outlined" label={t('buttons.cancel')} onClick={onCancel} />
      <ConfirmButton id="openSidebar" disabled={disabled} label={t('buttons.book-reservation')} onClick={onSave} />
    </ActionArea>
  )
}
